import { format, subDays } from 'date-fns';
import { supabase } from '@/integrations/supabase/client';
import type { TaskAgenda } from '@/types/taskAgenda';
import { clampTaskAgendaRangeToCurrentMonthSp, nextPayloadWithAutoSlot } from '@/services/taskAgenda/taskAgendaSchedule';
import { executeReprocessamentoCustosJob } from '@/services/taskAgenda/executeReprocessamentoCustosJob';

export type RunAgendaCustosOptions = {
  /** Preenchido só em disparo automático pela agenda (marca o slot do dia em payload). */
  automaticSlotKey?: string;
};

function buildCustosFilters(agenda: TaskAgenda): {
  dataInicio: string;
  dataFim: string;
  numeroOS: string;
  funcionario: string | undefined;
} {
  const raw = agenda.payload?.custosDaysRange;
  const days = typeof raw === 'number' && raw > 0 && raw <= 365 ? raw : 14;
  const end = new Date();
  const start = subDays(end, days);
  const numeroOS = typeof agenda.payload?.custosNumeroOs === 'string' ? agenda.payload.custosNumeroOs : '';
  const funcionario =
    typeof agenda.payload?.custosFuncionario === 'string' && agenda.payload.custosFuncionario.trim()
      ? agenda.payload.custosFuncionario.trim()
      : undefined;
  const { dataInicio, dataFim } = clampTaskAgendaRangeToCurrentMonthSp(
    format(start, 'yyyy-MM-dd'),
    format(end, 'yyyy-MM-dd')
  );
  return {
    dataInicio,
    dataFim,
    numeroOS,
    funcionario,
  };
}

/**
 * Executa reprocessamento de custos para uma linha da agenda e grava histórico em task_agenda_runs.
 */
export async function runAgendaReprocessamentoCustos(
  agenda: TaskAgenda,
  impostoPercentual: number,
  options?: RunAgendaCustosOptions
): Promise<void> {
  const filters = buildCustosFilters(agenda);
  const automaticSlotKey = options?.automaticSlotKey;

  const { data: runRow, error: insErr } = await supabase
    .from('task_agenda_runs')
    .insert({
      agenda_id: agenda.id,
      status: 'running',
      summary: null,
      error_message: null,
    })
    .select('id')
    .single();

  if (insErr || !runRow?.id) {
    const msg = insErr?.message || 'insert sem id';
    console.error('[task_agenda] Falha ao registrar execução (custos):', insErr);
    throw new Error(`Não foi possível registrar a execução na agenda: ${msg}`);
  }

  const runId = runRow.id as string;
  const nextPayload = () => nextPayloadWithAutoSlot(agenda, automaticSlotKey);

  try {
    const result = await executeReprocessamentoCustosJob({
      ...filters,
      impostoPercentual,
    });

    if (result.total === 0) {
      const summary = 'Nenhuma OS encontrada no período configurado.';
      await supabase
        .from('task_agenda_runs')
        .update({
          finished_at: new Date().toISOString(),
          status: 'success',
          summary,
          error_message: null,
        })
        .eq('id', runId);
      await supabase
        .from('task_agenda')
        .update({
          last_run_at: new Date().toISOString(),
          last_run_status: 'success',
          last_run_summary: summary,
          payload: nextPayload(),
        })
        .eq('id', agenda.id);
      return;
    }

    const summary = `Processadas: ${result.total}; atualizadas: ${result.updated}; erros: ${result.errors}`;
    await supabase
      .from('task_agenda_runs')
      .update({
        finished_at: new Date().toISOString(),
        status: 'success',
        summary,
        error_message: null,
      })
      .eq('id', runId);
    await supabase
      .from('task_agenda')
      .update({
        last_run_at: new Date().toISOString(),
        last_run_status: 'success',
        last_run_summary: summary,
        payload: nextPayload(),
      })
      .eq('id', agenda.id);
  } catch (e: unknown) {
    const msg = e instanceof Error ? e.message : String(e);
    await supabase
      .from('task_agenda_runs')
      .update({
        finished_at: new Date().toISOString(),
        status: 'error',
        summary: null,
        error_message: msg,
      })
      .eq('id', runId);
    await supabase
      .from('task_agenda')
      .update({
        last_run_at: new Date().toISOString(),
        last_run_status: 'error',
        last_run_summary: msg,
        payload: nextPayload(),
      })
      .eq('id', agenda.id);
    throw e;
  }
}
